import * as THREE from 'three';

export function createCodingModel(canvasElement, nodeColor, highlightColor) {
  const scene = new THREE.Scene();

  const renderer = new THREE.WebGLRenderer({
    canvas: canvasElement,
    alpha: true,
    context: canvasElement.getContext('webgl', { preserveDrawingBuffer: true }),
  });

  renderer.setPixelRatio(window.devicePixelRatio);

  const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
  camera.position.set(35, -10, 0);
  camera.lookAt(0, 0, 0);

  function resizeRenderer() {
    const bounds = canvasElement.getBoundingClientRect();
    const w = Math.max(1, bounds.width);
    const h = Math.max(1, bounds.height);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  resizeRenderer();
  setTimeout(resizeRenderer, 100);
  window.addEventListener('resize', resizeRenderer);

  // Lights
  scene.add(new THREE.AmbientLight(0xffffff, 0.1));

  const whiteLight = new THREE.PointLight(0xffffff, 50000);
  whiteLight.position.set(20, 60, 23);
  scene.add(whiteLight);

  const accentLight = new THREE.PointLight(highlightColor, 40000);
  accentLight.position.set(-15, 38, -50);
  scene.add(accentLight);

  // Materials
  const nodeMaterial = new THREE.MeshPhongMaterial({ color: nodeColor });
  const edgeMaterial = new THREE.MeshPhongMaterial({ color: nodeColor });

  const codingGroup = new THREE.Object3D();
  scene.add(codingGroup);

  // Helpers
  function createNode(position, radius = 0.8) {
    const geometry = new THREE.SphereGeometry(radius, 32, 32);
    const node = new THREE.Mesh(geometry, nodeMaterial.clone());
    node.position.copy(position);
    return node;
  }

  function createConnection(p1, p2, radius = 0.3) {
    const dir = new THREE.Vector3().subVectors(p2, p1);
    const len = dir.length();
    const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

    const geometry = new THREE.CylinderGeometry(radius, radius, len, 16);
    const conn = new THREE.Mesh(geometry, edgeMaterial.clone());
    conn.position.copy(mid);
    conn.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    return conn;
  }

  function createCube(parentGroup, size) {
    const cube = new THREE.Object3D();
    const half = size / 2;
    const fifth = size / 5;

    const corners = [
      [-1, -1, -1], [1, -1, -1], [1, 1, -1], [-1, 1, -1],
      [-1, -1, 1], [1, -1, 1], [1, 1, 1], [-1, 1, 1],
    ].map(([x, y, z]) => new THREE.Vector3(x * half, y * half, z * half));

    for (const pos of corners) cube.add(createNode(pos));

    const eye1 = new THREE.Vector3(half, fifth, fifth);
    const eye2 = new THREE.Vector3(half, fifth, -fifth);
    cube.add(createNode(eye1));
    cube.add(createNode(eye2));

    const mouth1 = new THREE.Vector3(half, -fifth, fifth);
    const mouth2 = new THREE.Vector3(half, -fifth, -fifth);
    cube.add(createConnection(mouth1, mouth2));

    const edges = [
      [0, 1], [1, 2], [2, 3], [3, 0],
      [4, 5], [5, 6], [6, 7], [7, 4],
      [0, 4], [1, 5], [2, 6], [3, 7],
    ];

    for (const [i, j] of edges) {
      cube.add(createConnection(corners[i], corners[j]));
    }

    parentGroup.add(cube);
  }

  createCube(codingGroup, 12);

  // Animation
  const mouse = new THREE.Vector2(0, 0);
  let t = 0;
  let rotX = 0;
  let rotY = 0;

  window.addEventListener('mousemove', (e) => {
    const bounds = canvasElement.getBoundingClientRect();
    // guard against hidden canvas (width/height = 0)
    const w = Math.max(1, bounds.width);
    const h = Math.max(1, bounds.height);

    mouse.x = ((e.clientX - bounds.left) / w) * 2 - 1;
    mouse.y = -((e.clientY - bounds.top) / h) * 2 + 1;
  });

  let animationId = null;
  let isActive = false;

  function animate() {
    if (!isActive) return;

    animationId = requestAnimationFrame(animate);
    t += 0.05;

    const maxTilt = 0.3;
    const targetX = mouse.y * maxTilt;
    const targetY = mouse.x * maxTilt;
    rotX += (targetX - rotX) * 0.04;
    rotY += (targetY - rotY) * 0.04;

    codingGroup.rotation.z = rotX;
    codingGroup.rotation.y = rotY;
    codingGroup.position.y = Math.sin(t * 0.8) * 0.5;

    renderer.render(scene, camera);
  }

  canvasElement.__threeResize = resizeRenderer;

  // Return controller object
  return {
    start: () => {
      if (!isActive) {
        isActive = true;
        animate();
      }
    },
    pause: () => {
      isActive = false;
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    },
    render: () => {
      if (isActive) {
        renderer.render(scene, camera);
      }
    }
  };
}
